'use client'

import { useState } from 'react'
import clsx from 'clsx'

import { Collapse } from 'shared/ui/Collapse'
import Checkbox from 'shared/ui/form/Checkbox'
import { CheckboxProps } from 'shared/ui/form/Checkbox/Checkbox'

import MarketingConsentCheckbox from './MarketingConsentCheckbox'

import styles from './MarketingConsentsCheckboxes.module.scss'

type IsNewsletterSubscriberValue = {
  isNewsletterSubscriber: boolean
}

type SmsConsentsGrantedValue = {
  smsConsentsGranted: boolean
}
export type AllMarketingConsentsValues = IsNewsletterSubscriberValue &
  SmsConsentsGrantedValue

export type MarketingConsentsValues =
  | IsNewsletterSubscriberValue
  | SmsConsentsGrantedValue
  | AllMarketingConsentsValues

interface AdditionalAgreementsProps
  extends Omit<
    CheckboxProps,
    | 'onChange'
    | 'values'
    | 'validate'
    | 'label'
    | 'labelAddon'
    | 'value'
    | 'name'
  > {
  values: MarketingConsentsValues
  onChange: (values: MarketingConsentsValues) => void
  isExpandedInitial?: boolean
  hideNewsletterAgreement?: boolean
  hideSmsConsentsAgreement?: boolean
}

const EXPANDED_AGREEMENTS_ID = 'additional-agreements-checkboxes'

const isNewsletterSubscriber = (
  values: MarketingConsentsValues
): boolean => {
  return 'isNewsletterSubscriber' in values && values.isNewsletterSubscriber
}

const smsConsentsGranted = (values: MarketingConsentsValues): boolean => {
  return 'smsConsentsGranted' in values && values.smsConsentsGranted
}

const MarketingConsentsCheckboxes = ({
  values,
  onChange,
  isExpandedInitial = false,
  hideNewsletterAgreement = false,
  hideSmsConsentsAgreement = false,
  ...props
}: AdditionalAgreementsProps) => {
  const [isExpanded, setIsExpanded] = useState(isExpandedInitial)

  const isCurrentAnyChecked =
    isNewsletterSubscriber(values) || smsConsentsGranted(values)

  const handleValueChange = (name: string, value: boolean) => {
    const newState = {
      ...values,
      [name]: value
    }

    onChange(newState)
  }

  if (hideNewsletterAgreement && hideSmsConsentsAgreement) {
    return null
  }

  return (
    <div>
      <MarketingConsentCheckbox
        name='masterConsent'
        value={isCurrentAnyChecked}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(prev => !prev)}
        controlsId={EXPANDED_AGREEMENTS_ID}
        labelClassName={clsx({ [styles.consentExpanded]: isExpanded })}
        onChange={(_, value) => {
          onChange({
            isNewsletterSubscriber: value,
            smsConsentsGranted: value
          })
        }}
        {...props}
      />
      <Collapse open={isExpanded}>
        <div
          className={styles.expandedAgreements}
          role='group'
          aria-label='Kanały zgody marketingowej'
          id={EXPANDED_AGREEMENTS_ID}
        >
          <div className={styles.checkboxWrapper}>
            {!hideNewsletterAgreement && (
              <Checkbox
                id='register_form_marketing_consent_newsletter'
                name='isNewsletterSubscriber'
                onChange={handleValueChange}
                value={isNewsletterSubscriber(values)}
                label='pocztą elektroniczną'
              />
            )}
            {!hideSmsConsentsAgreement && (
              <Checkbox
                id='register_form_marketing_consent_sms'
                name='smsConsentsGranted'
                onChange={handleValueChange}
                value={smsConsentsGranted(values)}
                label='drogą telefoniczną (w tym SMS i WhatsApp)'
              />
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default MarketingConsentsCheckboxes
